import { useEffect, useState } from 'react';
import { useTeam } from 'shared/context/teamContext';
import { isDemoTeamActive } from 'shared/utils/demoTeam';
import {
  isTeamKnownActivated,
  markTeamActivatedLocal,
  fetchTeamActivation,
} from 'shared/utils/onboardingActivation';

/**
 * Whether the current team has ingested its first run.
 *
 * This used to be answered by ActivationGate, which redirected an unactivated
 * team to a full-screen install wall. That wall took the sidebar with it, so a
 * team that had not instrumented yet could not reach Settings — they could not
 * invite a teammate, and could not upgrade to Pro until after they had wired up
 * tracing. Billing was locked behind the very step they might need help with.
 *
 * Now the shell always renders and only the page CONTENT is swapped, so this is
 * a question a layout asks rather than a redirect that fires.
 *
 * Activation is sticky and monotonic server-side (teams.activated_at), so a
 * team confirmed activated is cached for the tab session and never re-checked.
 *
 * @returns {{activated: boolean, pending: boolean, teamId: (string|number|null)}}
 *   `pending` means we genuinely do not know yet — callers should show a
 *   skeleton rather than guess, because guessing "unactivated" flashes the
 *   install panel at a team that has been live for months.
 */
export default function useTeamActivation() {
  const { selectedTeam, isLoading } = useTeam();
  const teamId = selectedTeam ? (selectedTeam.id ?? selectedTeam._id ?? null) : null;

  // Resolve synchronously where we can, so an activated team never sees a
  // skeleton on navigation.
  const initial = () => {
    if (isDemoTeamActive()) return { activated: true, pending: false };
    if (teamId != null && isTeamKnownActivated(teamId)) return { activated: true, pending: false };
    if (isLoading || teamId == null) return { activated: false, pending: true };
    if (selectedTeam?.activated === true) return { activated: true, pending: false };
    if (selectedTeam?.activated === false) return { activated: false, pending: false };
    return { activated: false, pending: true };
  };

  const [state, setState] = useState(initial);

  useEffect(() => {
    let cancelled = false;
    const next = initial();
    setState(next);

    // Only the genuinely-unknown case costs a request: the team payload landed
    // without an activation flag.
    if (!next.pending || isLoading || teamId == null) return undefined;

    (async () => {
      const { activated } = await fetchTeamActivation(teamId);
      if (cancelled) return;
      if (activated) markTeamActivatedLocal(teamId);
      setState({ activated, pending: false });
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, isLoading, selectedTeam?.activated]);

  return { ...state, teamId };
}
